import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";
import * as XLSX from "xlsx";
import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import { fetchAgentsThunk } from "../../../store/slices/agentsSlice";
import {
  fetchSlrmBillReportThunk,
  clearSlrmBillReport,
} from "../../../store/slices/slrmBillReportSlice";
import { printTableReport } from "./reportPrintUtils";
import "./ReportPage.css";
// Date helpers
const pad2 = (n) => String(n).padStart(2, "0");
const toIndiaDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const SlrmBillReportPage = () => {
  const dispatch = useDispatch();
  const wards = useSelector((s) => s.wards?.list || []);
  const agents = useSelector((s) => s.agents?.list || []);
  const { rows = [], loading, error } = useSelector(
    (s) => s.slrmBillReport || {}
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [wardId, setWardId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => {
    dispatch(fetchWardsThunk());
    dispatch(fetchAgentsThunk());
  }, [dispatch]);
  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );
        return currentField;
      }
      setSortDirection("asc");
      return field;
    });
    setPage(1);
  };
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }
    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searchedRows = !q
      ? [...rows]
      : rows.filter((r) => {
          const searchableText = `
            ${r.wb_id}
            ${r.qr_code}
            ${r.door_no}
            ${r.family_head}
            ${r.mtr_type}
            ${r.ward_name}
            ${r.ag_name}
            ${toIndiaDate(r.bill_date)}
              ${toIndiaDate(r.created_on)}
              ${toIndiaDate(r.slrm_last_bill_date)}
            ${toIndiaDate(r.disconnection_date)}
            ${r.slrm_fee}
              ${r.slrm_reading_fee}
            ${r.slrm_due}
            ${r.slrm_total}
          `.toLowerCase();
          return searchableText.includes(q);
        });
    if (!sortField) {
      return searchedRows;
    }
    const numericFields = [
      "wb_id",
      "slrm_fee",
      "slrm_reading_fee",
      "slrm_due",
      "slrm_total",
    ];
    const dateFields = [
      "bill_date",
      "created_on",
      "slrm_last_bill_date",
      "disconnection_date",
    ];
    return [...searchedRows].sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];
      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);
        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      if (dateFields.includes(sortField)) {
        const aTime = aValue ? new Date(aValue).getTime() : 0;
        const bTime = bValue ? new Date(bValue).getTime() : 0;
        return sortDirection === "asc"
          ? aTime - bTime
          : bTime - aTime;
      }
      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, search, sortField, sortDirection]);
  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, wardId, agentId, search, rows.length]);
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);
  const handleLoad = () => {
    dispatch(
      fetchSlrmBillReportThunk({
        from_date: fromDate,
        to_date: toDate,
        ward_id: wardId,
        agent_id: agentId,
      })
    );
  };
  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setWardId("");
    setAgentId("");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    dispatch(clearSlrmBillReport());
  };
  const exportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "Bill No": r.wb_id,
      "House No": r.qr_code,
      "Door No": r.door_no,
      Name: r.family_head,
      "Mtr Type": r.mtr_type,
      Ward: r.ward_name,
      Agent: r.ag_name,
      "Bill Date": toIndiaDate(r.bill_date),
      "Bill Generated Date": toIndiaDate(r.created_on),
      "Last SLRM Bill Date": toIndiaDate(r.slrm_last_bill_date),
      "Disc Date": toIndiaDate(r.disconnection_date),
      "SLRM Fee": r.slrm_fee,
      "SLRM Reading Fee": r.slrm_reading_fee ?? 0,
      "SLRM Due": r.slrm_due,
      "SLRM Total": r.slrm_total,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SLRM Bill Report");
    XLSX.writeFile(wb, "slrm_bill_report.xlsx");
  };
  const exportPDF = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "Bill No": r.wb_id ?? "",
      "House No": r.qr_code ?? "",
      "Door No": r.door_no ?? "",
      Name: r.family_head ?? "",
      "Mtr Type": r.mtr_type ?? "",
      Ward: r.ward_name ?? "",
      Agent: r.ag_name ?? "",
      "Bill Date": toIndiaDate(r.bill_date),
      "Bill Generated Date": toIndiaDate(r.created_on),
      "Last SLRM Bill Date": toIndiaDate(r.slrm_last_bill_date),
      "Disc Date": toIndiaDate(r.disconnection_date),
      "SLRM Fee": r.slrm_fee ?? "",
      "SLRM Reading Fee": r.slrm_reading_fee ?? "",
      "SLRM Due": r.slrm_due ?? "",
      "SLRM Total": r.slrm_total ?? "",
    }));
    printTableReport({
      title: "SLRM Bill - Report",
fileName: "slrm_bill_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );
    return <Pagination className="mb-0">{items}</Pagination>;
  };
  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">SLRM Bill - Report</div>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {error ? <div className="alert alert-danger mb-3">{error}</div> : null}
          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="report-label">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="report-label">Ward</Form.Label>
                <Form.Select
                  value={wardId}
                  onChange={(e) => setWardId(e.target.value)}
                >
                  <option value="">Select Ward</option>
                  {wards.map((w) => (
                    <option key={w.ward_id} value={String(w.ward_id)}>
                      {w.ward_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="report-label">Agent</Form.Label>
                <Form.Select
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                >
                  <option value="">Select Agent</option>
                  {agents.map((a) => (
                    <option key={a.agent_id} value={String(a.agent_id)}>
                      {a.ag_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={12} className="d-flex align-items-end">
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>
                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    <RefreshCcw size={16} className="me-2" />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
          <div className="report-table-toolbar">
            <div className="report-table-title">
              SLRM Bills{" "}
              <span className="report-count">
                ({total.toLocaleString()} records)
              </span>
            </div>
            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search house / name / ward / agent..."
                />
              </InputGroup>
              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                Excel
              </Button>
              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                PDF
              </Button>
            </div>
          </div>
          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table hover className="mb-0 report-table">
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "wb_id" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("wb_id")}
                      >
                        # {renderSortIcon("wb_id")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "wb_id" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("wb_id")}
                      >
                        Bill No {renderSortIcon("wb_id")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "qr_code" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("qr_code")}
                      >
                        House No {renderSortIcon("qr_code")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "door_no" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("door_no")}
                      >
                        Door No {renderSortIcon("door_no")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "family_head" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("family_head")}
                      >
                        Name {renderSortIcon("family_head")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "mtr_type" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("mtr_type")}
                      >
                        Mtr type {renderSortIcon("mtr_type")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "ward_name" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("ward_name")}
                      >
                        Ward {renderSortIcon("ward_name")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "ag_name" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("ag_name")}
                      >
                        Agent {renderSortIcon("ag_name")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "bill_date" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("bill_date")}
                      >
                        Bill Date {renderSortIcon("bill_date")}
                      </button>
                    </th>
                  <th>
                    <button type="button"
                      className={`report-sort-btn ${sortField === "created_on" ? "is-active" : ""}`}
                      onClick={() => handleSort("created_on")}
                    >
                      Bill Generated Date {renderSortIcon("created_on")}
                    </button>
                  </th>
                  <th>
                    <button type="button"
                      className={`report-sort-btn ${sortField === "slrm_last_bill_date" ? "is-active" : ""}`}
                      onClick={() => handleSort("slrm_last_bill_date")}
                    >
                      Last SLRM Bill Date {renderSortIcon("slrm_last_bill_date")}
                    </button>
                  </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "disconnection_date" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("disconnection_date")}
                      >
                        Disc Date {renderSortIcon("disconnection_date")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_fee" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("slrm_fee")}
                      >
                        SLRM Fee {renderSortIcon("slrm_fee")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_reading_fee" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("slrm_reading_fee")}
                      >
                        SLRM Reading Fee {renderSortIcon("slrm_reading_fee")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_due" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("slrm_due")}
                      >
                        SLRM Due {renderSortIcon("slrm_due")}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_total" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("slrm_total")}
                      >
                        SLRM Total {renderSortIcon("slrm_total")}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="16" className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((r, idx) => (
                      <tr key={String(r.wb_id || idx)}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{r.wb_id}</td>
                        <td>{r.qr_code}</td>
                        <td>{r.door_no}</td>
                        <td>{r.family_head}</td>
                        <td>{r.mtr_type}</td>
                        <td>{r.ward_name}</td>
                        <td>{r.ag_name}</td>
                        <td>{toIndiaDate(r.bill_date)}</td>
                        <td>{toIndiaDate(r.created_on)}</td>
                        <td>{toIndiaDate(r.slrm_last_bill_date)}</td>
                        <td>{toIndiaDate(r.disconnection_date)}</td>
                        <td>{r.slrm_fee}</td>
                        <td>{r.slrm_reading_fee ?? 0}</td>
                        <td>{r.slrm_due}</td>
                        <td>{r.slrm_total}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="16" className="text-center py-4">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>
                <Form.Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="report-pagination">{renderPagination()}</div>
              <div className="report-page-info">
                Page {currentPage} / {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};
export default SlrmBillReportPage;
